import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../pages/Loader";
import {
  updateProjectCheckList,
  getProjectCheckListApproved,
  deleteProjectCheckList,
  getLastWorkedOn,
  getChecklistCommentCount,
} from "../API";
import {
  formatUTCDateToMMDDYYYY,
  extractDateFromISO,
} from "../utils/utils";
import AddNewCheckList from "./AddNewCheckList";
import StatusChangeNotesModal from "../components/modal/StatusChangeNotesModal";
import DeleteCheckListWarningModal from "./modal/DeleteCheckListWarningModal";
import { ACTIONS, STATUS, canRecall } from "../utils/checklistStatus";
import "../assets/styles/project-checklist.scss";

// Generic fallback copy for a rejected StatusNoteRequiredError when the
// server's `message` is absent. Neither Complete nor Recall requires a note
// today (NG14 — Recall stays optional), so this branch is defence-in-depth
// (TRD §2.3.3, §3.5) rather than an expected path.
const NOTE_REQUIRED_FALLBACK_MESSAGE =
  "A reason is required to complete this action.";

// ==========================================
// ProjectGroupCard (Nested Parent)
// ==========================================
const ProjectGroupCard = ({ group, expandSignal, collapseSignal, renderCardFn }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const sharedWO = group.items[0]?.work_order_number;

  // Listen for expand/collapse all signals from the parent
  useEffect(() => {
    if (expandSignal > 0) setIsExpanded(true);
  }, [expandSignal]);

  useEffect(() => {
    if (collapseSignal > 0) setIsExpanded(false);
  }, [collapseSignal]);

  return (
    <div className="nested-parent-card" >
      <div className="parent-header"
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <button type="button" className="expand-toggle" >
              {isExpanded ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M300.3 199.2C312.9 188.9 331.4 189.7 343.1 201.4L471.1 329.4C480.3 338.6 483 352.3 478 364.3C473 376.3 461.4 384 448.5 384L192.5 384C179.6 384 167.9 376.2 162.9 364.2C157.9 352.2 160.7 338.5 169.9 329.4L297.9 201.4L300.3 199.2z"/></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M300.3 440.8C312.9 451 331.4 450.3 343.1 438.6L471.1 310.6C480.3 301.4 483 287.7 478 275.7C473 263.7 461.4 256 448.5 256L192.5 256C179.6 256 167.9 263.8 162.9 275.8C157.9 287.8 160.7 301.5 169.9 310.6L297.9 438.6L300.3 440.8z"/></svg>
              )}
            </button>

            <h3>{group.projectName}</h3>
          </div>
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#2563eb", backgroundColor: "#dbeafe", padding: "2px 8px", borderRadius: "9999px" }}>
              {group.items.length}
            </span>
          </div>
        </div>
        <div className="d-flex parent-subheading">
          <p style={{ margin: 0 }}><span>WO:</span> {sharedWO || "-"}</p>
        </div>
      </div>

      {/* Nested Children */}
      {isExpanded && (
        <div className="nested-child-container" >
          {group.items.map(card => renderCardFn(card))}
        </div>
      )}
    </div>
  );
};

const MyProjectCheckList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCardId, setLoadingCardId] = useState(null);
  const [checkListOpen, setCheckListOpen] = useState([]);
  const [checkListInReview, setCheckListInReview] = useState([]);
  const [checkListProcessing, setCheckListProcessing] = useState([]);
  const [isBilledSortAsc, setIsBilledSortAsc] = useState(false);
  // Board-track status-change state (TRD §2.4, CLNOTE-F005): the single
  // channel between a card's Complete/Recall button and
  // StatusChangeNotesModal. Replaces `selectedJobToComplete`.
  const [pendingAction, setPendingAction] = useState(null); // {action, fromStatus, toStatus, checklist} | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [selectedCheckListToDelete, setSelectedCheckListToDelete] = useState({});
  const [searchText, setSearchText] = useState("");
  const [filteredCheckListOpen, setFilteredCheckListOpen] = useState([]);
  const [filteredCheckListInReview, setFilteredCheckListInReview] = useState([]);
  const [filteredCheckListProcessing, setFilteredCheckListProcessing] = useState([]);
  const [lastWorkedOnDates, setLastWorkedOnDates] = useState({});
  const [isLastWorkedOnLoading, setIsLastWorkedOnLoading] = useState(true);

  // Signals for expanding/collapsing all cards per column
  const [expandOpenSignal, setExpandOpenSignal] = useState(0);
  const [collapseOpenSignal, setCollapseOpenSignal] = useState(0);
  const [isAllOpenExpanded, setIsAllOpenExpanded] = useState(true);

  const [expandReviewSignal, setExpandReviewSignal] = useState(0);
  const [collapseReviewSignal, setCollapseReviewSignal] = useState(0);
  const [isAllReviewExpanded, setIsAllReviewExpanded] = useState(true);

  const [expandApprovedSignal, setExpandApprovedSignal] = useState(0);
  const [collapseApprovedSignal, setCollapseApprovedSignal] = useState(0);
  const [isAllApprovedExpanded, setIsAllApprovedExpanded] = useState(true);

  const navigate = useNavigate();
  const role = localStorage.getItem("role_name");
  const user_id = JSON.parse(localStorage.getItem("user_id"));
  // Checklist approvers see every checklist even when their login role
  // resolved to a crew role (Foreman/GF), which would otherwise scope the
  // fetch to their own crew assignments. Same flag-driven gating as UnitJob.
  const isChecklistApprover = JSON.parse(localStorage.getItem("checklist_approver") || "false");

  const fetchApprovedProjectCheckLists = useCallback(async (isBilled) => {
    const approvedProjectChecklistFilter = isBilled ? "billed" : "unbilled";

    if (!isChecklistApprover && user_id && role === "Foreman") {
      const query = `foreman_user_id=${user_id}&expand=project%2Cforeman_user%2Cgeneral_foreman_user`;
      return getProjectCheckListApproved(approvedProjectChecklistFilter, query);
    } else if (!isChecklistApprover && user_id && role === "General Foreman") {
      const query = `general_foreman_user_id=${user_id}&expand=project%2Cforeman_user%2Cgeneral_foreman_user`;
      return getProjectCheckListApproved(approvedProjectChecklistFilter, query);
    } else if (!isChecklistApprover && user_id && role === "Foreman GeneralForeman") {
      const foremanQuery = `foreman_user_id=${user_id}&expand=project%2Cforeman_user%2Cgeneral_foreman_user`;
      const generalForemanQuery = `general_foreman_user_id=${user_id}&expand=project%2Cforeman_user%2Cgeneral_foreman_user`;

      const [foremanResults, generalForemanResults] = await Promise.all([
        getProjectCheckListApproved(approvedProjectChecklistFilter, foremanQuery),
        getProjectCheckListApproved(approvedProjectChecklistFilter, generalForemanQuery)
      ]);

      const combinedData = [...(foremanResults?.data || []), ...(generalForemanResults?.data || [])];
      const uniqueData = combinedData.filter((item, index, self) =>
        index === self.findIndex(t => t.id === item.id)
      );

      return { data: uniqueData };
    }

    const query = "expand=project%2Cforeman_user%2Cgeneral_foreman_user";
    return getProjectCheckListApproved(approvedProjectChecklistFilter, query);
  }, [role, user_id, isChecklistApprover]);

  const getApprovedProjectCheckListDetails = useCallback(async(isBilled) => {
    const approvedProjectChecklist = await fetchApprovedProjectCheckLists(isBilled);
    const statusApproved = (approvedProjectChecklist?.data || []).filter((item) =>
      item?.status === 4 || item?.status === 5
    );

    setCheckListProcessing(statusApproved);
  }, [fetchApprovedProjectCheckLists]);

  const getProjectCheckListDetails = useCallback(async (isBilled = false) => {
    setIsLoading(true);
    try {
      const approvedProjectChecklist = await fetchApprovedProjectCheckLists(isBilled);
      const checkListData = approvedProjectChecklist?.data || [];

      const statusApproved = checkListData.filter((item) =>
        item?.status === 4 || item?.status === 5
      );
      setCheckListProcessing(statusApproved);

      setCheckListOpen(checkListData.filter((item) =>
        item?.status === 0 || item?.status === 1
      ));
      setCheckListInReview(checkListData.filter((item) =>
        item?.status === 2 || item?.status === 3
      ));
    } finally {
      setIsLoading(false);
    }
  }, [fetchApprovedProjectCheckLists]);

  useEffect(() => {
    getProjectCheckListDetails();
  }, [getProjectCheckListDetails]);

  useEffect(() => {
    const filterData = (data) => {
      if (!searchText.trim()) {
        return data;
      }
      return data.filter(item =>
        item?.work_order_number?.toLowerCase().includes(searchText.toLowerCase())
      );
    };

    setFilteredCheckListOpen(filterData(checkListOpen));
    setFilteredCheckListInReview(filterData(checkListInReview));
    setFilteredCheckListProcessing(filterData(checkListProcessing));
  }, [searchText, checkListOpen, checkListInReview, checkListProcessing]);

  useEffect(() => {
    setIsLastWorkedOnLoading(true);
    getLastWorkedOn()
      .then((res) => {
        const dataArray = res?.data?.data || res?.data || res;
        if (Array.isArray(dataArray)) {
          const datesMap = {};
          dataArray.forEach((item) => {
            const pId = item.project_id || item.projectId;
            const cId = item.crew_id || item.crewId;
            const lwo = item.last_worked_on || item.lastWorkedOn;

            if (pId && cId) {
              datesMap[`${pId}_${cId}`] = lwo;
            }
          });
          setLastWorkedOnDates(datesMap);
        } else {
          console.warn("getLastWorkedOn did not return an array:", dataArray);
        }
      })
      .catch((err) => console.error("Error fetching last worked on:", err))
      .finally(() => setIsLastWorkedOnLoading(false));
  }, []);

  // F7 board comment-count badge (TRD Appendix C.2, D10, CLCMT-F009). One
  // batched request for every visible card's id, fired whenever the board's
  // card set reloads (the same three lists the card-fetch effect above
  // populates) — never per-card, never on an interval (AC-F7.2).
  //
  // R1 capability gate: an older backend without the batched count route
  // 404s, and baseApi.js's interceptor toasts + *resolves* an unnamed 404
  // with a non-response value rather than rejecting (TRD §1.1 finding 2),
  // so `typeof res?.data === "undefined"` is what distinguishes a real
  // payload from that quiet-failure shape. `commentCounts` simply stays
  // unset for anything it didn't hear back about, which self-gates every
  // badge off (AC-F7.1) without needing a separate availability flag.
  const [commentCounts, setCommentCounts] = useState({});

  useEffect(() => {
    const ids = [...checkListOpen, ...checkListInReview, ...checkListProcessing]
      .map((item) => item?.id)
      .filter((id) => id !== undefined && id !== null);

    if (ids.length === 0) {
      setCommentCounts({});
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await getChecklistCommentCount(ids);
        if (cancelled) return;
        if (typeof res?.data === "undefined") {
          return;
        }
        const rows = Array.isArray(res.data) ? res.data : [];
        const counts = {};
        rows.forEach((row) => {
          counts[row.project_check_list_id] = row.count;
        });
        setCommentCounts(counts);
      } catch (error) {
        // Falls into the R1 capability gate — older backend, missing route,
        // or (in tests) an unmocked helper. Badges simply do not render.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkListOpen, checkListInReview, checkListProcessing]);

  const refreshData = async () => {
    await getProjectCheckListDetails(isBilledSortAsc);
  };

  const onCardClick = (card) => {
    navigate(`/unit-job/${card.id}`);
  };

  // Complete and Recall no longer call updateProjectCheckList directly
  // (CLNOTE-F005). Each one only stages a `pendingAction` and opens
  // StatusChangeNotesModal; the actual PUT happens in
  // handlePendingActionConfirm once the user confirms. Neither action
  // requires a note (NG14 — Recall stays optional); card visibility gating
  // for these buttons is untouched (NG18) — only what a click *does* has
  // changed.
  const openRecallAction = (check_list) => {
    setServerError(null);
    setPendingAction({
      action: ACTIONS.RECALL,
      fromStatus: check_list?.status,
      toStatus: STATUS.IN_PROGRESS,
      checklist: check_list,
    });
  };

  const openCompleteAction = (check_list) => {
    setServerError(null);
    setPendingAction({
      action: ACTIONS.COMPLETE,
      fromStatus: check_list?.status,
      toStatus: STATUS.SUBMITTED,
      checklist: check_list,
    });
  };

  const handlePendingActionCancel = () => {
    if (isSubmitting) return;
    setPendingAction(null);
    setServerError(null);
  };

  // Single confirm handler for both board transitions (Complete, Recall).
  // Sends the existing PUT payload plus `status_note` (trimmed note, or null
  // when empty). A rejected 400 StatusNoteRequiredError — propagated by the
  // baseApi.js interceptor change (CLNOTE-F003) — is caught here and
  // surfaced inline via the modal's `serverError` prop instead of a toast,
  // with the typed note preserved because the modal stays open. This branch
  // shouldn't normally fire for these optional-note actions, but is wired
  // for robustness (TRD §2.3.3).
  const handlePendingActionConfirm = async (note) => {
    if (!pendingAction) return;
    setIsSubmitting(true);
    setServerError(null);
    try {
      const res = await updateProjectCheckList(pendingAction.checklist?.id, {
        status: pendingAction.toStatus,
        status_note: note ?? null,
      });
      if (res?.status === 200) {
        toast.success("CheckList Status Updated");
        setIsSubmitting(false);
        setPendingAction(null);
        refreshData();
        return;
      }
      toast.error("Failed to update checklist status");
      setIsSubmitting(false);
    } catch (error) {
      if (error?.response?.data?.name === "StatusNoteRequiredError") {
        setServerError(
          error?.response?.data?.message || NOTE_REQUIRED_FALLBACK_MESSAGE
        );
        setIsSubmitting(false);
        return;
      }
      console.error(
        `Error confirming ${pendingAction.action} for project checklist:`,
        error
      );
      toast.error("Failed to update checklist status");
      setIsSubmitting(false);
    }
  };

  const handleBilledToggle = async (check_list) => {
    setLoadingCardId(check_list.id);

    const newBilledAt = check_list?.billed_at ? null : extractDateFromISO(new Date().toISOString());
    const originalBilledAt = check_list?.billed_at;

    const updateCardState = (prev) =>
      prev.map((item) =>
        item.id === check_list.id ? { ...item, billed_at: newBilledAt } : item
      );

    setCheckListOpen(updateCardState);
    setCheckListInReview(updateCardState);
    setCheckListProcessing(updateCardState);

    try {
      const payload = {
        billed_at: newBilledAt,
      };

      const res = await updateProjectCheckList(check_list?.id, payload);

      if (res?.status === 200) {
        toast.success("Billing Status Changed");
      } else {
        throw new Error("API update failed");
      }
    } catch (error) {
      console.error("Error updating billing status:", error);
      toast.error("Failed to change billing status. Reverting...");

      const revertCardState = (prev) =>
        prev.map((item) =>
          item.id === check_list.id ? { ...item, billed_at: originalBilledAt } : item
        );

      setCheckListOpen(revertCardState);
      setCheckListInReview(revertCardState);
      setCheckListProcessing(revertCardState);
    } finally {
      setLoadingCardId(null);
    }
  };

  const onClickDownloadReport = (card) => {
    navigate(`/unit-job-report/${card.id}`);
  }

  const selectCheckListToDelete = (check_list) => {
    setSelectedCheckListToDelete(check_list);
  }

  const deleteCheckListPromise = async (id) => {
    try {
      const res = await deleteProjectCheckList(id);
      if (res?.status === 204) {
        toast.success("Project Checklist Deleted Successfully");
        refreshData();
      }
    } catch (error) {
      console.error("Error deleting project checklist:", error);
      toast.error("Failed to delete project checklist");
    }
  };

  // Toggle Handlers
  const toggleAllOpen = () => {
    if (isAllOpenExpanded) setCollapseOpenSignal(p => p + 1);
    else setExpandOpenSignal(p => p + 1);
    setIsAllOpenExpanded(!isAllOpenExpanded);
  };

  const toggleAllReview = () => {
    if (isAllReviewExpanded) setCollapseReviewSignal(p => p + 1);
    else setExpandReviewSignal(p => p + 1);
    setIsAllReviewExpanded(!isAllReviewExpanded);
  };

  const toggleAllApproved = () => {
    if (isAllApprovedExpanded) setCollapseApprovedSignal(p => p + 1);
    else setExpandApprovedSignal(p => p + 1);
    setIsAllApprovedExpanded(!isAllApprovedExpanded);
  };

  const renderGroupedCards = (list, showDelete, showReturn, showBilledToggle, showJobComplete, expandSignal, collapseSignal) => {
    const grouped = list.reduce((acc, item) => {
      const projectId = item.project_id || 'Unknown';
      if (!acc[projectId]) {
        acc[projectId] = {
          projectName: item?.project?.description || `Project ${projectId}`,
          items: []
        };
      }
      acc[projectId].items.push(item);
      return acc;
    }, {});

    return Object.values(grouped).map((group, index) => (
      <ProjectGroupCard
        key={group.projectName || index}
        group={group}
        expandSignal={expandSignal}
        collapseSignal={collapseSignal}
        renderCardFn={(card) => renderCard(card, showDelete, showReturn, showBilledToggle, showJobComplete)}
      />
    ));
  };

  const renderCard = (card, showDelete = false, showReturn = false, showBilledToggle = false, showJobComplete = false) => {
    const isCardLoading = loadingCardId === card.id;
    const crewId = card?.crew_id;
    const foremanName = card?.foreman_user_name || "";
    const genForemanName = card?.general_foreman_user_name || "";

    const projectId = card?.project_id;
    const lastWorkedOnKey = `${projectId}_${crewId}`;
    const lazyLastWorkedOn = lastWorkedOnDates[lastWorkedOnKey];
    const displayLastWorkedOn = lazyLastWorkedOn || card?.last_worked_on;

    return (
      <div key={card.id} className="nested-child" >

        {/* Visual connection arrow to parent */}
        <div className="nested-child-symbol">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 0 0 4 4h12"/></svg>
        </div>

        {/* Top Row: Pole & Actions (Delete/GF Tick) */}
        <div className="d-flex justify-content-between align-items-center nested-child-header">
          <div className="d-flex align-items-center" style={{ gap: "6px", fontSize: "13px", color: "#334155", fontWeight: "500" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
            Pole: <span style={{ fontWeight: "700", color: "#0f172a" }}>{card?.pole_number || "-"}</span>
            {typeof commentCounts[card.id] === "number" && commentCounts[card.id] > 0 && (
              // Reuses the existing neutral `.checklist-count` pill
              // (project-checklist.scss) — no new styling — so it is
              // visually distinct from the coloured status/GF-tick
              // indicators nearby without introducing a new palette
              // (AC-F7.1, AC-F7.4).
              <span
                className="checklist-count"
                aria-label={`${commentCounts[card.id]} comments`}
                title={`${commentCounts[card.id]} comments`}
              >
                {commentCounts[card.id]}
              </span>
            )}
          </div>

          {showDelete ? (
            role === "Admin" && (
              <div onClick={() => {selectCheckListToDelete(card);}} style={{width: "20px", cursor: "pointer"}} data-bs-toggle="modal" data-bs-target="#deleteCheckListWarningModal">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path fill="rgb(230, 105, 105)" d="M262.2 48C248.9 48 236.9 56.3 232.2 68.8L216 112L120 112C106.7 112 96 122.7 96 136C96 149.3 106.7 160 120 160L520 160C533.3 160 544 149.3 544 136C544 122.7 533.3 112 520 112L424 112L407.8 68.8C403.1 56.3 391.2 48 377.8 48L262.2 48zM128 208L128 512C128 547.3 156.7 576 192 576L448 576C483.3 576 512 547.3 512 512L512 208L464 208L464 512C464 520.8 456.8 528 448 528L192 528C183.2 528 176 520.8 176 512L176 208L128 208zM288 280C288 266.7 277.3 256 264 256C250.7 256 240 266.7 240 280L240 456C240 469.3 250.7 480 264 480C277.3 480 288 469.3 288 456L288 280zM400 280C400 266.7 389.3 256 376 256C362.7 256 352 266.7 352 280L352 456C352 469.3 362.7 480 376 480C389.3 480 400 469.3 400 456L400 280z"/></svg>
              </div>
            )
          ) : (
            <div className="recall-tick-btn">
              <span className={card?.is_reviewed ? `green-tick` : `grey-tick`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path></svg>
                GF
              </span>
            </div>
          )}
        </div>

        {/* Middle Row: Crew Info */}
        <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ marginBottom: "10px" }}>
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <div style={{ backgroundColor: "#f1f5f9", padding: "8px", borderRadius: "6px" }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><path d="M16 3.128a4 4 0 0 1 0 7.744"></path><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><circle cx="9" cy="7" r="4"></circle></svg>
            </div>
            <div>
              <div style={{ fontSize: "10px", fontWeight: "600", color: "#94a3b8", textTransform: "uppercase", marginBottom: "2px" }}>Crew ID</div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b" }}>{crewId}</div>
            </div>
          </div>

          <div style={{ textAlign: "right", fontSize: "12px" }}>
            <div style={{ color: "#1e293b", marginBottom: "2px" }}><span style={{ fontWeight: "700", color: "#94a3b8", marginRight: "4px" }}>FM</span>{foremanName}</div>
            <div style={{ color: "#1e293b" }}><span style={{ fontWeight: "700", color: "#94a3b8", marginRight: "4px" }}>GF</span>{genForemanName}</div>
          </div>
        </div>

        {/* Bottom Row: Last Worked / Billed & Action Buttons */}
        <div className="d-flex justify-content-between align-items-center nested-child-footer" >

          {/* Left side of bottom row */}
          <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
            {showBilledToggle ? (
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <span>Billed:</span>
                {(isChecklistApprover || role !== "Foreman") && (
                  <span className="toggle-container billed" style={{ margin: 0 }}>
                    <input
                      onClick={() => { handleBilledToggle(card); }}
                      name="is_hourly"
                      type="checkbox"
                      id={`switch-${card.id}`}
                      checked={card?.billed_at != null ? true: false}
                      disabled={isCardLoading}
                    />
                    <label htmlFor={`switch-${card.id}`}></label>
                  </span>
                )}
              </div>
            ) : (
              <div className="d-flex align-items-center" style={{ gap: "4px" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {isLastWorkedOnLoading ? "Loading..." : (displayLastWorkedOn ? formatUTCDateToMMDDYYYY(displayLastWorkedOn) : "N/A")}
              </div>
            )}
          </div>

          {/* Right side of bottom row (Buttons) */}
          <div className="d-flex" style={{ gap: "8px" }}>
            <button onClick={() => { onCardClick(card); }} className="open view-btn" style={{ padding: "4px 12px", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px", border: "1px solid #e2e8f0", borderRadius: "6px", backgroundColor: "#fff", color: "#475569" }}>
              View
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg>
            </button>

            {showReturn && canRecall(card, user_id) && (
              <button onClick={() => { openRecallAction(card); }} className="submit-btn" disabled={isCardLoading} >
                Recall
                <svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" fill="white" viewBox="0 0 640 640"><path d="M552 256L408 256C398.3 256 389.5 250.2 385.8 241.2C382.1 232.2 384.1 221.9 391 215L437.7 168.3C362.4 109.7 253.4 115 184.2 184.2C109.2 259.2 109.2 380.7 184.2 455.7C259.2 530.7 380.7 530.7 455.7 455.7C463.9 447.5 471.2 438.8 477.6 429.6C487.7 415.1 507.7 411.6 522.2 421.7C536.7 431.8 540.2 451.8 530.1 466.3C521.6 478.5 511.9 490.1 501 501C401 601 238.9 601 139 501C39.1 401 39 239 139 139C233.3 44.7 382.7 39.4 483.3 122.8L535 71C541.9 64.1 552.2 62.1 561.2 65.8C570.2 69.5 576 78.3 576 88L576 232C576 245.3 565.3 256 552 256z"/></svg>
              </button>
            )}

            {showJobComplete && (
              <button onClick={() => { openCompleteAction(card); }} className="submit-btn" disabled={isCardLoading} >
                Complete
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path></svg>
              </button>
            )}

            {!showReturn && !showJobComplete && (
              <button disabled={isCardLoading} className="submit-btn" onClick={() => onClickDownloadReport(card)} >
                Report
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15V3"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="m7 10 5 5 5-5"></path></svg>
              </button>
            )}
          </div>
        </div>

        {isCardLoading && (
          <div className="bottom-loading-bar" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", backgroundColor: "#3b82f6" }}></div>
        )}
      </div>
    );
  };

  return !isLoading ? (
    <div className="checklist-data-wrapper project-details-page checklist-page">
      <div className="checklist-data">
        <div className="project-details-toolbar">
          <h1 className="title project-details-title">My Project Checklist</h1>
          <div className="project-details-toolbar__actions checklist-board-actions">
            <div className="search-container">
              <form className="form-wo">
                <button>
                  <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-labelledby="search">
                    <path d="M7.667 12.667A5.333 5.333 0 107.667 2a5.333 5.333 0 000 10.667zM14.334 14l-2.9-2.9" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                </button>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Work Order Number"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    border: "0",
                    background: "transparent",
                    fontSize: "13px",
                    width: "150px",
                    padding: "0.5rem 0"
                  }}
                />
                <button className="reset" type="reset" onClick={() => setSearchText("")}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </form>
            </div>
            <button
              type="button"
              className="primary-btn"
              data-bs-toggle="modal"
              data-bs-target="#addNewChecklist"
            >
              + Add New Checklist
            </button>
          </div>
        </div>

        <div className="checklist-flex">
          {/* OPEN SECTION */}
          <div className="checklist-card status-open">
            <div className="checklist-header" >
              <div className="checklist-header-left">
                <span className="checklist-status-dot"></span>
                <h3 className="txt">Open</h3>
                <span className="checklist-count">
                  {filteredCheckListOpen?.length || 0}
                </span>
              </div>
              <div className="d-flex header-action">
                <button
                  type="button"
                  className="checklist-expand-toggle"
                  onClick={(e) => { e.preventDefault(); toggleAllOpen(); }}
                  title={isAllOpenExpanded ? "Collapse All" : "Expand All"}
                >
                  <svg width="24" height="24" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isAllOpenExpanded ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.3s ease" }}>
                    <path d="M14.4216 12H11V14H15.472C15.7583 13.9128 15.8767 13.5196 15.6455 13.2782L14.4216 12Z" fill="#757575"/>
                    <path d="M13.464 20H11V18H15.379L13.464 20Z" fill="#757575"/>
                    <path d="M16.233 20L17.09 19.1049C17.3947 18.7866 17.5912 18.4032 17.6865 18H28V20H16.233Z" fill="#757575"/>
                    <path d="M17.722 14C17.8326 13.3082 17.6653 12.5663 17.1847 12H28V14H17.722Z" fill="#757575"/>
                    <path d="M11 24H28V26H11V24Z" fill="#757575"/>
                    <path d="M11.6835 21.8731C11.5216 22.0423 11.259 22.0423 11.0971 21.8731L7.12228 17.7218C6.86106 17.449 7.04607 16.9825 7.41549 16.9825L15.3651 16.9825C15.7346 16.9825 15.9196 17.449 15.6584 17.7218L11.6835 21.8731Z" fill="#757575"/>
                    <path d="M11.0972 9.12684C11.2591 8.95772 11.5217 8.95772 11.6836 9.12684L15.6584 13.2782C15.9197 13.551 15.7347 14.0175 15.3652 14.0175L7.41558 14.0175C7.04616 14.0175 6.86115 13.551 7.12237 13.2782L11.0972 9.12684Z" fill="#757575"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="checklist-content">
              {filteredCheckListOpen?.length > 0 ? (
                renderGroupedCards(filteredCheckListOpen, true, false, false, true, expandOpenSignal, collapseOpenSignal)
              ) : (
                <div className="checklist-empty">No open checklists</div>
              )}
              <button
                type="button"
                className="checklist-add-card-btn"
                data-bs-toggle="modal"
                data-bs-target="#addNewChecklist"
              >
                + Add New Checklist
              </button>
            </div>
          </div>

          {/* IN REVIEW SECTION */}
          <div className="checklist-card status-inReview">
            <div className="checklist-header" >
              <div className="checklist-header-left">
                <span className="checklist-status-dot"></span>
                <h3 className="txt">In Review</h3>
                <span className="checklist-count">
                  {filteredCheckListInReview?.length || 0}
                </span>
              </div>
              <div className="d-flex header-action">
                <button
                  type="button"
                  className="checklist-expand-toggle"
                  onClick={(e) => { e.preventDefault(); toggleAllReview(); }}
                  title={isAllReviewExpanded ? "Collapse All" : "Expand All"}
                >
                  <svg width="24" height="24" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isAllReviewExpanded ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.3s ease" }}>
                    <path d="M14.4216 12H11V14H15.472C15.7583 13.9128 15.8767 13.5196 15.6455 13.2782L14.4216 12Z" fill="#757575"/>
                    <path d="M13.464 20H11V18H15.379L13.464 20Z" fill="#757575"/>
                    <path d="M16.233 20L17.09 19.1049C17.3947 18.7866 17.5912 18.4032 17.6865 18H28V20H16.233Z" fill="#757575"/>
                    <path d="M17.722 14C17.8326 13.3082 17.6653 12.5663 17.1847 12H28V14H17.722Z" fill="#757575"/>
                    <path d="M11 24H28V26H11V24Z" fill="#757575"/>
                    <path d="M11.6835 21.8731C11.5216 22.0423 11.259 22.0423 11.0971 21.8731L7.12228 17.7218C6.86106 17.449 7.04607 16.9825 7.41549 16.9825L15.3651 16.9825C15.7346 16.9825 15.9196 17.449 15.6584 17.7218L11.6835 21.8731Z" fill="#757575"/>
                    <path d="M11.0972 9.12684C11.2591 8.95772 11.5217 8.95772 11.6836 9.12684L15.6584 13.2782C15.9197 13.551 15.7347 14.0175 15.3652 14.0175L7.41558 14.0175C7.04616 14.0175 6.86115 13.551 7.12237 13.2782L11.0972 9.12684Z" fill="#757575"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="checklist-content">
              {filteredCheckListInReview?.length > 0 ? (
                renderGroupedCards(filteredCheckListInReview, false, true, false, false, expandReviewSignal, collapseReviewSignal)
              ) : (
                <div className="checklist-empty">No checklists in review</div>
              )}
            </div>
          </div>

          {/* APPROVED SECTION */}
          <div className="checklist-card status-approved">
            <div className="checklist-header">
              <div className="checklist-header-left">
                <span className="checklist-status-dot"></span>
                <h3 className="txt">Approved</h3>
                <span className="checklist-count">
                  {filteredCheckListProcessing?.length || 0}
                </span>
              </div>
              <div className="d-flex align-items-center header-action">
                <button
                  type="button"
                  className={`billed-filter-chip ${isBilledSortAsc ? "active" : ""}`}
                  onClick={() => {
                    setIsBilledSortAsc((prev) => !prev);
                    getApprovedProjectCheckListDetails(!isBilledSortAsc);
                  }}
                >
                  Billed
                </button>
                <div className="d-flex">
                  <button
                    type="button"
                    className="checklist-expand-toggle"
                    onClick={(e) => { e.preventDefault(); toggleAllApproved(); }}
                    title={isAllApprovedExpanded ? "Collapse All" : "Expand All"}
                  >
                    <svg width="24" height="24" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isAllApprovedExpanded ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.3s ease" }}>
                      <path d="M14.4216 12H11V14H15.472C15.7583 13.9128 15.8767 13.5196 15.6455 13.2782L14.4216 12Z" fill="#757575"/>
                      <path d="M13.464 20H11V18H15.379L13.464 20Z" fill="#757575"/>
                      <path d="M16.233 20L17.09 19.1049C17.3947 18.7866 17.5912 18.4032 17.6865 18H28V20H16.233Z" fill="#757575"/>
                      <path d="M17.722 14C17.8326 13.3082 17.6653 12.5663 17.1847 12H28V14H17.722Z" fill="#757575"/>
                      <path d="M11 24H28V26H11V24Z" fill="#757575"/>
                      <path d="M11.6835 21.8731C11.5216 22.0423 11.259 22.0423 11.0971 21.8731L7.12228 17.7218C6.86106 17.449 7.04607 16.9825 7.41549 16.9825L15.3651 16.9825C15.7346 16.9825 15.9196 17.449 15.6584 17.7218L11.6835 21.8731Z" fill="#757575"/>
                      <path d="M11.0972 9.12684C11.2591 8.95772 11.5217 8.95772 11.6836 9.12684L15.6584 13.2782C15.9197 13.551 15.7347 14.0175 15.3652 14.0175L7.41558 14.0175C7.04616 14.0175 6.86115 13.551 7.12237 13.2782L11.0972 9.12684Z" fill="#757575"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="checklist-content">
              {filteredCheckListProcessing?.length > 0 ? (
                renderGroupedCards(filteredCheckListProcessing, false, false, true, false, expandApprovedSignal, collapseApprovedSignal)
              ) : (
                <div className="checklist-empty">No approved checklists</div>
              )}
            </div>
          </div>
        </div>

        <AddNewCheckList
          refreshData={refreshData}
          setIsLoading={setIsLoading}
        />
        <StatusChangeNotesModal
          action={pendingAction?.action}
          fromStatus={pendingAction?.fromStatus}
          toStatus={pendingAction?.toStatus}
          isOpen={Boolean(pendingAction)}
          onConfirm={handlePendingActionConfirm}
          onCancel={handlePendingActionCancel}
          serverError={serverError}
          busy={isSubmitting}
        />
        <DeleteCheckListWarningModal
          selectedCheckList={selectedCheckListToDelete}
          deleteCheckListPromise={deleteCheckListPromise}
        />
      </div>
    </div>
  ) : (
    <Loader />
  );
};

export default MyProjectCheckList;
